import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import {
  ConsultationVitalsSection,
  emptyVitalsFormState,
  formStateToVitalsInput,
} from "@/components/clinical/consultation-vitals-form";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { listActiveStays } from "@/core/hospitalization/hospitalization-store";
import { billingSnapshotForAdmission } from "@/core/billing/service-billing-status";
import { completeActiveTicketsForPatient } from "@/core/queue/service-queue-store";
import {
  createVitalSignRecord,
  getLatestVitalSignForPatient,
  listVitalSignRecords,
  validateVitalSignRecord,
  VitalSignValidationError,
} from "@/core/nursing/vital-sign-store";
import {
  formatVitalSignRecordSummary,
  VITAL_SIGN_CONTEXT_LABELS,
  VITAL_SIGN_STATUS_LABELS,
  type VitalSignContext,
  type VitalSignRecord,
} from "@/core/nursing/types";
import { getPatientById, listPatients, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import {
  canValidateNursingVitals,
  canWriteNursingVitals,
} from "@/core/permissions";
import { listQueueTickets } from "@/core/queue/service-queue-store";
import { routes } from "@/router/all_routes";

export function NursingCarePage() {
  const auth = useAuth();
  const canWrite = canWriteNursingVitals(auth);
  const canValidate = canValidateNursingVitals(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [patientId, setPatientId] = useState("");
  const [context, setContext] = useState<VitalSignContext>("admission");
  const [admissionId, setAdmissionId] = useState("");
  const [stayId, setStayId] = useState("");
  const [notes, setNotes] = useState("");
  const [vitalsForm, setVitalsForm] = useState(emptyVitalsFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const activeStays = useMemo(() => {
    void refreshKey;
    return listActiveStays();
  }, [refreshKey]);

  const records = useMemo(() => {
    void refreshKey;
    return listVitalSignRecords();
  }, [refreshKey]);

  const recordsPagination = useClientPagination(records, [], 25);
  const { pagedItems: pagedRecords } = recordsPagination;

  const nursingQueue = useMemo(() => {
    void refreshKey;
    return listQueueTickets({
      serviceId: "nursing",
      status: ["waiting", "called", "in_progress"],
    });
  }, [refreshKey]);

  const queuePatientIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ticket of nursingQueue) ids.add(ticket.patientId);
    for (const stay of activeStays) ids.add(stay.patientId);
    return ids;
  }, [nursingQueue, activeStays]);

  const selectablePatients = useMemo(() => {
    void refreshKey;
    if (queuePatientIds.size === 0) return [];
    return listPatients().filter((p) => queuePatientIds.has(p.id));
  }, [refreshKey, queuePatientIds]);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:nursing", refresh);
    window.addEventListener("hospitol:queue", refresh);
    window.addEventListener("hospitol:hospitalization", refresh);
    return () => {
      window.removeEventListener("hospitol:nursing", refresh);
      window.removeEventListener("hospitol:queue", refresh);
      window.removeEventListener("hospitol:hospitalization", refresh);
    };
  }, []);

  const refresh = () => setRefreshKey((k) => k + 1);

  const staffName =
    auth.user?.displayName ??
    [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(" ") ??
    auth.user?.email ??
    "Infirmier(ère)";

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    if (!patientId) {
      setFormError("Sélectionnez un patient.");
      return;
    }
    try {
      createVitalSignRecord({
        patientId,
        recordedById: auth.user?.id ?? "staff-local",
        recordedByName: staffName,
        context,
        admissionId: admissionId || null,
        stayId: stayId || null,
        vitals: formStateToVitalsInput(vitalsForm, null),
        notes,
      });
      completeActiveTicketsForPatient("nursing", patientId);
      setVitalsForm(emptyVitalsFormState());
      setNotes("");
      setFormError(null);
      setSavedMsg("Constantes enregistrées.");
      refresh();
      window.setTimeout(() => setSavedMsg(null), 2500);
    } catch (err) {
      setFormError(
        err instanceof VitalSignValidationError ? err.message : "Enregistrement impossible.",
      );
    }
  };

  const pickFromQueue = (ticketPatientId: string, ticketAdmissionId: string | null, motif: string) => {
    setPatientId(ticketPatientId);
    setAdmissionId(ticketAdmissionId ?? "");
    setContext(/urgence/i.test(motif) ? "emergency" : "admission");
    setStayId("");
  };

  const pickFromStay = (stayPatientId: string, stayAdmissionId: string | null, id: string) => {
    setPatientId(stayPatientId);
    setStayId(id);
    setAdmissionId(stayAdmissionId ?? "");
    setContext("ward_round");
  };

  const selectedPatient = patientId ? getPatientById(patientId) : null;
  const latestForPatient = patientId ? getLatestVitalSignForPatient(patientId) : null;

  return (
    <div>
      <PageHeader
        title="Soins infirmiers"
        description="Prise des constantes vitales — admission, urgences, tournées et pré-op."
        actions={
          <div className="ph-page-header-actions">
            <Link to={routes.hospitalization} className="btn btn-outline-secondary btn-sm">
              Hospitalisation & lits
            </Link>
          </div>
        }
      />

      {savedMsg && <div className="alert alert-success py-2">{savedMsg}</div>}

      <ServiceQueuePanel
        serviceId="nursing"
        defaultUnitName="Prise des constantes"
        title="File d'attente — soins infirmiers"
      />

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-5">
          <div className="ph-invoice-sheet mb-3">
            <h3 className="h6 fw-semibold mb-2">Patients à voir</h3>
            {nursingQueue.length === 0 && activeStays.length === 0 ? (
              <p className="small text-muted mb-0">Aucun patient en attente.</p>
            ) : (
              <ul className="list-unstyled small mb-0">
                {nursingQueue.map((t) => (
                  <li key={t.id} className="mb-2 pb-2 border-bottom">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-start"
                      onClick={() => pickFromQueue(t.patientId, t.admissionId, t.motif)}
                    >
                      {patientFullName(
                        getPatientById(t.patientId) ?? { firstName: "?", lastName: "" },
                      )}{" "}
                      — {t.unitName}
                    </button>
                  </li>
                ))}
                {activeStays.map((stay) => {
                  const p = getPatientById(stay.patientId);
                  return (
                    <li key={stay.id} className="mb-2 pb-2 border-bottom">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-start"
                        onClick={() => pickFromStay(stay.patientId, stay.admissionId, stay.id)}
                      >
                        {p ? patientFullName(p) : "—"} — Tournée ({stay.wardName})
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="ph-invoice-sheet">
            <h3 className="h6 fw-semibold mb-2">Derniers relevés</h3>
            {records.length === 0 ? (
              <p className="small text-muted mb-0">Aucune constante enregistrée.</p>
            ) : (
              <>
              <ul className="list-unstyled small mb-0">
                {pagedRecords.map((record) => (
                  <VitalRecordListItem
                    key={record.id}
                    record={record}
                    canValidate={canValidate}
                    onValidate={() => {
                      validateVitalSignRecord(record.id);
                      refresh();
                    }}
                  />
                ))}
              </ul>
              <TablePagination
                page={recordsPagination.page}
                pageSize={recordsPagination.pageSize}
                totalItems={recordsPagination.totalItems}
                onPageChange={recordsPagination.setPage}
                onPageSizeChange={recordsPagination.setPageSize}
              />
              </>
            )}
          </div>
        </div>

        <div className="col-lg-7">
          <div className="ph-invoice-sheet">
            <h3 className="h6 fw-semibold mb-3">Nouveau relevé</h3>
            {!canWrite && (
              <div className="alert alert-warning py-2 small">
                Droits insuffisants pour saisir les constantes (
                <code>hospitol.nursing.vitals.write</code>).
              </div>
            )}
            <form onSubmit={handleSave}>
              <div className="row g-2 mb-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold" htmlFor="nursing-patient">
                    Patient
                  </label>
                  <select
                    id="nursing-patient"
                    className="form-select form-select-sm"
                    value={patientId}
                    disabled={!canWrite}
                    onChange={(e) => setPatientId(e.target.value)}
                  >
                    <option value="">
                      {selectablePatients.length === 0
                        ? "— Orientez d'abord depuis l'accueil —"
                        : "— Choisir dans la file —"}
                    </option>
                    {selectablePatients.map((p) => (
                      <option key={p.id} value={p.id}>
                        {patientFullName(p)} ({p.fileNumber})
                      </option>
                    ))}
                  </select>
                  {selectablePatients.length === 0 && (
                    <p className="small text-muted mb-0 mt-1">
                      Seuls les patients orientés vers « Soins infirmiers — constantes » ou en
                      tournée hospitalisation apparaissent ici.
                    </p>
                  )}
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold" htmlFor="nursing-context">
                    Contexte
                  </label>
                  <select
                    id="nursing-context"
                    className="form-select form-select-sm"
                    value={context}
                    disabled={!canWrite}
                    onChange={(e) => setContext(e.target.value as VitalSignContext)}
                  >
                    {(Object.keys(VITAL_SIGN_CONTEXT_LABELS) as VitalSignContext[]).map((c) => (
                      <option key={c} value={c}>
                        {VITAL_SIGN_CONTEXT_LABELS[c]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedPatient && latestForPatient && (
                <p className="small text-muted mb-2">
                  Dernier relevé : {formatVitalSignRecordSummary(latestForPatient)} ·{" "}
                  <Link to={patientDetailPath(selectedPatient.id)}>Dossier</Link>
                </p>
              )}

              <ConsultationVitalsSection
                form={vitalsForm}
                vitals={null}
                canEdit={canWrite}
                idPrefix="nursing-vitals"
                onChange={(patch) => setVitalsForm((prev) => ({ ...prev, ...patch }))}
              />

              <label className="form-label small fw-semibold mt-2" htmlFor="nursing-notes">
                Notes infirmières
              </label>
              <textarea
                id="nursing-notes"
                className="form-control form-control-sm mb-3"
                rows={2}
                disabled={!canWrite}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              {formError && (
                <div className="alert alert-warning py-2 small">{formError}</div>
              )}

              {canWrite && (
                <button type="submit" className="btn btn-primary btn-sm">
                  Enregistrer les constantes
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function VitalRecordListItem({
  record,
  canValidate,
  onValidate,
}: {
  record: VitalSignRecord;
  canValidate: boolean;
  onValidate: () => void;
}) {
  const patient = getPatientById(record.patientId);
  const nursingBilling = record.admissionId
    ? billingSnapshotForAdmission(record.admissionId, "nursing")
    : null;
  return (
    <li className="mb-2 pb-2 border-bottom">
      <div className="fw-medium">
        {patient ? patientFullName(patient) : "—"} ·{" "}
        {VITAL_SIGN_CONTEXT_LABELS[record.context]}
      </div>
      <div className="text-muted">{formatVitalSignRecordSummary(record)}</div>
      <div className="d-flex align-items-center gap-2 mt-1">
        <span className="badge text-bg-light border">
          {VITAL_SIGN_STATUS_LABELS[record.status]}
        </span>
        <span className="text-muted">
          {new Date(record.recordedAt).toLocaleString("fr-FR")} — {record.recordedByName}
        </span>
        {canValidate && record.status === "recorded" && (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onValidate}>
            Valider
          </button>
        )}
        {nursingBilling && (
          <ServiceBillingBadge snapshot={nursingBilling} compact />
        )}
      </div>
    </li>
  );
}
